import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Chip, Typography } from '@mui/material';
import { TravelerBuilding, TravelerTruss } from '../types';

interface ProgressSummaryProps {
  building: TravelerBuilding;
}

function getSectionSummary(trusses: TravelerTruss[]) {
  if (trusses.length === 0) return null;
  const complete = trusses.filter(t => t.isLocked).length;
  const total = trusses.length;

  if (complete === total) {
    return {
      text: `Complete (${complete} of ${total})`,
      color: '#27ae60',
      icon: true,
    };
  }
  if (complete > 0) {
    return {
      text: `${complete} of ${total} complete`,
      color: '#f39c12',
      icon: false,
    };
  }
  // A stage has a real tracker record when its id > 0 (placeholder stages use negative ids).
  const anyStarted = trusses.some(t => t.productionStages.some(s => s.id > 0));
  if (anyStarted) {
    return { text: 'In Progress', color: '#f39c12', icon: false };
  }
  return { text: 'Not Started', color: '#bdc3c7', icon: false };
}

const ProgressSummary = ({ building }: ProgressSummaryProps) => {
  const allComplete = building.completionStatus === 'complete';

  const sections = [
    { label: 'Gable Trusses', trusses: building.gableTrusses },
    { label: 'Header Trusses', trusses: building.headerTrusses },
    { label: 'Lean-To Trusses', trusses: building.leanToTrusses },
  ].filter(s => s.trusses.length > 0);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '8px',
        p: 2.5,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
          Progress Summary
        </Typography>
        {allComplete && (
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label="ALL TRUSSES COMPLETE"
            size="small"
            sx={{
              bgcolor: '#27ae60',
              color: '#fff',
              fontWeight: 600,
              fontSize: '11px',
              '& .MuiChip-icon': { color: '#fff' },
            }}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 4 }}>
        {sections.map(section => {
          const summary = getSectionSummary(section.trusses);
          if (!summary) return null;
          return (
            <Box key={section.label}>
              <Typography sx={{ fontSize: '12px', color: '#666', mb: 0.5 }}>
                {section.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: summary.color,
                }}
              >
                {summary.icon && '✓ '}
                {summary.text}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProgressSummary;
